import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { HubConnectionState } from "@microsoft/signalr";
import { ListChecks, ListOrdered, Loader2, Lock, NotebookPen, Pencil, Plus, Trash2 } from "lucide-react";
import {
    ConnectApiError, connectParityApi, normalizeUserId,
    type MeetingChatMember, type MeetingNoteBlock, type MeetingNoteKind, type NoteChangeResult
} from "@/api/connectApi";
import { useMeetingChatMembers } from "@/api/hooks";
import { MeetingsEvents, useMeetingsHub, type NoteLock, type UseMeetingsHub } from "@/api/useMeetingsHub";
import { useAccessToken } from "@/auth/useAccessToken";
import { renderMarkdown } from "@/lib/markdown";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import { cn } from "@/lib/utils";
import { colourFor, initials } from "../chat/chatUi";

const NO_EVENTS = {};
/** A lock we haven't heard a heartbeat for in this long is treated as gone. */
const LOCK_TTL_MS = 60_000;
const HEARTBEAT_MS = 15_000;

type Changes = Partial<Pick<MeetingNoteBlock, "text" | "assigneeUserId" | "dueDate" | "done">>;

interface NotesCtx {
    myUserId: string;
    members: MeetingChatMember[];
    lockOf: (blockId: string) => NoteLock | null;
    acquire: (blockId: string) => Promise<boolean>;
    release: (blockId: string) => void;
    save: (block: MeetingNoteBlock, changes: Changes, version?: number) => Promise<NoteChangeResult>;
    remove: (block: MeetingNoteBlock) => Promise<NoteChangeResult>;
    create: (kind: MeetingNoteKind, text: string, extra?: { assigneeUserId?: string | null; dueDate?: string | null }) => Promise<MeetingNoteBlock>;
    autoEdit: string | null;
    setAutoEdit: (id: string | null) => void;
}

const byPosition = (a: MeetingNoteBlock, b: MeetingNoteBlock) =>
    a.position - b.position || a.createdAt.localeCompare(b.createdAt);

const todayIso = () => new Date().toLocaleDateString("en-CA");

/**
 * Teams' "Meeting notes": a shared agenda, notes and action items for one
 * meeting — in the room's side panel, the calendar's meeting details and the
 * Recap. Everyone who can see the meeting's chat can edit. Each block is
 * edited by one person at a time (their avatar shows on it) and saves carry
 * the version they started from, so a clash reloads that block instead of
 * silently overwriting it. Text is plain / light Markdown, always escaped.
 *
 * In the room it rides the room's hub connection; elsewhere it opens its own.
 */
export function MeetingNotes({ meetingId, hub, className }: { meetingId: string; hub?: UseMeetingsHub; className?: string }) {
    return hub
        ? <NotesBody meetingId={meetingId} hub={hub} className={className} />
        : <NotesWithOwnHub meetingId={meetingId} className={className} />;
}

function NotesWithOwnHub({ meetingId, className }: { meetingId: string; className?: string }) {
    const hub = useMeetingsHub(NO_EVENTS);
    return <NotesBody meetingId={meetingId} hub={hub} className={className} />;
}

function NotesBody({ meetingId, hub, className }: { meetingId: string; hub: UseMeetingsHub; className?: string }) {
    const token = useAccessToken();
    const auth = useAuth();
    const myUserId = normalizeUserId(auth.user?.profile.sub);
    const qc = useQueryClient();
    const key = useMemo(() => ["meeting-notes", meetingId, token] as const, [meetingId, token]);
    const notes = useQuery({
        queryKey: key,
        queryFn: () => connectParityApi.meetingNotes(meetingId, token),
        enabled: Boolean(token),
        // The hub keeps it live; this is only a safety net.
        refetchInterval: 60_000
    });
    const members = useMeetingChatMembers(meetingId);
    const [locks, setLocks] = useState<Record<string, NoteLock & { at: number }>>({});
    const [autoEdit, setAutoEdit] = useState<string | null>(null);
    const hubRef = useRef(hub);
    hubRef.current = hub;

    const upsert = useCallback((block: MeetingNoteBlock) => {
        qc.setQueryData<MeetingNoteBlock[]>(key, (prev) => {
            const list = prev ?? [];
            const i = list.findIndex((b) => b.id === block.id);
            if (i < 0) return [...list, block].sort(byPosition);
            if (list[i]!.version > block.version) return list;
            const next = list.slice();
            next[i] = block;
            return next;
        });
    }, [qc, key]);
    const drop = useCallback((id: string) => {
        qc.setQueryData<MeetingNoteBlock[]>(key, (prev) => (prev ?? []).filter((b) => b.id !== id));
    }, [qc, key]);

    // Watch this meeting's notes while connected; a reconnect refetches what it may have missed.
    const watchedOnce = useRef(false);
    useEffect(() => {
        if (hub.state !== HubConnectionState.Connected) return;
        let cancelled = false;
        void hubRef.current.watchNotes(meetingId).then((current) => {
            if (cancelled || !current) return;
            const now = Date.now();
            setLocks(Object.fromEntries(current.map((l) => [l.blockId, { ...l, at: now }])));
        });
        if (watchedOnce.current) void qc.invalidateQueries({ queryKey: ["meeting-notes", meetingId] });
        watchedOnce.current = true;
        return () => {
            cancelled = true;
            void hubRef.current.unwatchNotes(meetingId);
        };
    }, [hub.state, hub.connectionId, meetingId, qc]);

    useEffect(() => {
        const same = (id: unknown) => typeof id === "string" && normalizeUserId(id) === normalizeUserId(meetingId);
        const offChanged = hub.on(MeetingsEvents.NotesChanged, (payload) => {
            const p = payload as { meetingId?: string; reason?: string; blockId?: string; block?: MeetingNoteBlock | null };
            if (!same(p.meetingId)) return;
            if (p.reason === "delete" && p.blockId) drop(p.blockId);
            else if (p.block) upsert(p.block);
        });
        const offEditing = hub.on(MeetingsEvents.NotesEditing, (payload) => {
            const p = payload as NoteLock & { meetingId?: string; editing?: boolean };
            if (!same(p.meetingId) || !p.blockId) return;
            setLocks((prev) => {
                const next = { ...prev };
                if (p.editing) next[p.blockId] = { ...p, at: Date.now() };
                else if (next[p.blockId]?.connectionId === p.connectionId) delete next[p.blockId];
                return next;
            });
        });
        const prune = window.setInterval(() => setLocks((prev) => {
            const cutoff = Date.now() - LOCK_TTL_MS;
            const kept = Object.entries(prev).filter(([, l]) => l.at >= cutoff);
            return kept.length === Object.keys(prev).length ? prev : Object.fromEntries(kept);
        }), 10_000);
        return () => {
            offChanged();
            offEditing();
            window.clearInterval(prune);
        };
    }, [hub.on, meetingId, upsert, drop]);

    const ctx: NotesCtx = {
        myUserId,
        members: members.data ?? [],
        lockOf: (blockId) => {
            const l = locks[blockId];
            return l && l.connectionId !== hub.connectionId ? l : null;
        },
        acquire: async (blockId) => (await hubRef.current.notesEditing(meetingId, blockId, true)) !== false,
        release: (blockId) => { void hubRef.current.notesEditing(meetingId, blockId, false); },
        save: async (block, changes, version = block.version) => {
            const r = await connectParityApi.updateMeetingNote(meetingId, block.id, {
                version,
                text: changes.text ?? block.text,
                assigneeUserId: "assigneeUserId" in changes ? changes.assigneeUserId ?? null : block.assigneeUserId ?? null,
                dueDate: "dueDate" in changes ? changes.dueDate ?? null : block.dueDate ?? null,
                done: changes.done ?? block.done
            }, token);
            if (r.ok) {
                if (r.block) upsert(r.block);
            } else if (r.current) {
                upsert(r.current);
            } else if (r.status === 404) {
                drop(block.id);
            }
            return r;
        },
        remove: async (block) => {
            const r = await connectParityApi.deleteMeetingNote(meetingId, block.id, block.version, token);
            if (r.ok) drop(block.id);
            else if (r.current) upsert(r.current);
            return r;
        },
        create: async (kind, text, extra) => {
            const b = await connectParityApi.createMeetingNote(meetingId, { kind, text, ...extra }, token);
            upsert(b);
            return b;
        },
        autoEdit,
        setAutoEdit
    };

    if (notes.isPending) {
        return <p className={cn("flex items-center gap-2 text-xs text-muted-foreground", className)}><Loader2 className="h-3 w-3 animate-spin" /> Loading notes…</p>;
    }
    if (notes.isError) {
        const notFound = notes.error instanceof Error && / → 404$/.test(notes.error.message);
        return (
            <p role="alert" className={cn("text-sm text-destructive", className)}>
                {notFound ? "Meeting notes are only available to people in this meeting." : "Couldn't load the meeting notes."}
            </p>
        );
    }

    const all = notes.data ?? [];
    const agenda = all.filter((b) => b.kind === "agenda");
    const sections = all.filter((b) => b.kind === "note");
    const actions = all.filter((b) => b.kind === "action").sort((a, b) => Number(a.done) - Number(b.done) || byPosition(a, b));

    return (
        <div className={cn("space-y-5", className)}>
            <Section title="Agenda" Icon={ListOrdered} count={agenda.length}>
                {agenda.length > 0 ? (
                    <ol className="space-y-1.5">
                        {agenda.map((b, i) => (
                            <li key={b.id} className="flex gap-2">
                                <span className="mt-2 w-5 shrink-0 text-right text-xs font-semibold text-muted-foreground">{i + 1}.</span>
                                <TextBlock block={b} ctx={ctx} className="flex-1" />
                            </li>
                        ))}
                    </ol>
                ) : null}
                <AddLine ctx={ctx} kind="agenda" placeholder="Add an agenda item" />
            </Section>

            <Section title="Notes" Icon={NotebookPen} count={sections.length}>
                {sections.map((b) => <TextBlock key={b.id} block={b} ctx={ctx} />)}
                <AddNoteSection ctx={ctx} empty={sections.length === 0} />
            </Section>

            <Section title="Action items" Icon={ListChecks} count={actions.filter((a) => !a.done).length}>
                {actions.length > 0 ? (
                    <ul className="space-y-1.5">
                        {actions.map((b) => <ActionRow key={b.id} block={b} ctx={ctx} />)}
                    </ul>
                ) : null}
                <AddAction ctx={ctx} />
            </Section>
        </div>
    );
}

function Section({ title, Icon, count, children }: { title: string; Icon: typeof ListChecks; count: number; children: React.ReactNode }) {
    return (
        <section aria-label={title} className="space-y-2">
            <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Icon className="h-3.5 w-3.5" aria-hidden /> {title}{count > 0 ? ` · ${count}` : ""}
            </h3>
            {children}
        </section>
    );
}

/** "Bijo is editing" — their avatar with a lock. */
function LockBadge({ name }: { name: string }) {
    return (
        <span
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-500/15 py-0.5 pl-0.5 pr-2 text-[11px] font-medium text-amber-800 dark:text-amber-200"
            title={`${name} is editing`}
        >
            <span className={cn("flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold text-white", colourFor(name))} aria-hidden>
                {initials(name)}
            </span>
            <Lock className="h-3 w-3" aria-hidden />
            <span className="sr-only">{name} is editing this</span>
            <span aria-hidden className="max-w-[6rem] truncate">{name.split(" ")[0]}</span>
        </span>
    );
}

/**
 * Editing one block: take the soft lock (and keep it alive), then save with
 * the version you started from. A clash keeps your text and shows theirs, so
 * saving again is a deliberate overwrite, not an accident.
 */
function useBlockEditor(block: MeetingNoteBlock, ctx: NotesCtx) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const [base, setBase] = useState(block.version);
    const [conflict, setConflict] = useState<MeetingNoteBlock | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const heartbeat = useRef<number | null>(null);
    const editingRef = useRef(false);
    const ctxRef = useRef(ctx);
    ctxRef.current = ctx;

    const stopHeartbeat = () => {
        if (heartbeat.current !== null) window.clearInterval(heartbeat.current);
        heartbeat.current = null;
    };

    const begin = useCallback(async () => {
        const lock = ctxRef.current.lockOf(block.id);
        if (lock) {
            setError(`${lock.displayName} is editing this right now.`);
            return;
        }
        setError(null);
        if (!(await ctxRef.current.acquire(block.id))) {
            setError("Someone else just started editing this. Try again in a moment.");
            return;
        }
        setDraft(block.text);
        setBase(block.version);
        setConflict(null);
        setEditing(true);
        editingRef.current = true;
        stopHeartbeat();
        heartbeat.current = window.setInterval(() => void ctxRef.current.acquire(block.id), HEARTBEAT_MS);
    }, [block.id, block.text, block.version]);

    const end = useCallback(() => {
        stopHeartbeat();
        if (editingRef.current) ctxRef.current.release(block.id);
        editingRef.current = false;
        setEditing(false);
        setConflict(null);
    }, [block.id]);

    // Unmounting mid-edit gives the block back.
    useEffect(() => () => {
        stopHeartbeat();
        if (editingRef.current) ctxRef.current.release(block.id);
    }, [block.id]);

    const submit = async (changes: Changes) => {
        setBusy(true);
        setError(null);
        const r = await ctxRef.current.save(block, changes, base);
        setBusy(false);
        if (r.ok) {
            end();
            return;
        }
        if (r.current) {
            setConflict(r.current);
            setBase(r.current.version);
            return;
        }
        setError(r.message);
    };

    return { editing, draft, setDraft, conflict, busy, error, setError, begin, end, submit };
}

function ConflictNotice({ current }: { current: MeetingNoteBlock }) {
    return (
        <div role="alert" className="mb-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs">
            <p>
                <strong>{current.updatedByName || "Someone"}</strong> changed this while you were editing. Their version is below;
                saving now replaces it with yours.
            </p>
            <p className="mt-1 max-h-24 overflow-y-auto whitespace-pre-wrap break-words rounded bg-background/60 px-2 py-1 text-muted-foreground">
                {current.text || "(empty)"}
            </p>
        </div>
    );
}

/** An agenda item (one line) or a notes section (Markdown). */
function TextBlock({ block, ctx, className }: { block: MeetingNoteBlock; ctx: NotesCtx; className?: string }) {
    const ed = useBlockEditor(block, ctx);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const lock = ctx.lockOf(block.id);
    const isLine = block.kind !== "note";
    const label = isLine ? "agenda item" : "notes section";
    const { autoEdit, setAutoEdit } = ctx;
    const begin = ed.begin;

    // A freshly added notes section opens straight into editing.
    useEffect(() => {
        if (autoEdit !== block.id) return;
        setAutoEdit(null);
        void begin();
    }, [autoEdit, block.id, setAutoEdit, begin]);

    const save = () => {
        if (isLine && !ed.draft.trim()) {
            ed.setError("Type something, or delete the item.");
            return;
        }
        void ed.submit({ text: ed.draft });
    };

    const html = useMemo(() => (isLine ? "" : sanitizeHtml(renderMarkdown(block.text))), [isLine, block.text]);

    return (
        <div className={cn("group rounded-md border bg-card px-3 py-2", ed.editing && "border-primary", className)}>
            {ed.editing ? (
                <div>
                    {ed.conflict ? <ConflictNotice current={ed.conflict} /> : null}
                    {isLine ? (
                        <input
                            autoFocus
                            value={ed.draft}
                            maxLength={500}
                            aria-label="Agenda item"
                            onChange={(e) => ed.setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); save(); }
                                if (e.key === "Escape") { e.preventDefault(); ed.end(); }
                            }}
                            className="h-8 w-full rounded-md border bg-background px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                    ) : (
                        <textarea
                            autoFocus
                            value={ed.draft}
                            maxLength={10_000}
                            rows={6}
                            aria-label="Notes"
                            onChange={(e) => ed.setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); save(); }
                                if (e.key === "Escape") { e.preventDefault(); ed.end(); }
                            }}
                            placeholder="Write notes… **bold**, _italic_, - lists"
                            className="w-full resize-y rounded-md border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button type="button" onClick={save} disabled={ed.busy} className="inline-flex h-7 items-center gap-1 rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground disabled:opacity-50">
                            {ed.busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Save
                        </button>
                        <button type="button" onClick={ed.end} className="h-7 rounded-md border px-3 text-xs hover:bg-accent">Cancel</button>
                        <span className="text-[11px] text-muted-foreground">{isLine ? "Enter to save · Esc to cancel" : "Ctrl+Enter to save · Esc to cancel"}</span>
                    </div>
                </div>
            ) : (
                <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1" onDoubleClick={() => { if (!lock) void ed.begin(); }}>
                        {isLine ? (
                            <p className="whitespace-pre-wrap break-words text-sm">{block.text}</p>
                        ) : block.text.trim() ? (
                            <div className="break-words text-sm [&_a]:text-primary [&_a]:underline" dangerouslySetInnerHTML={{ __html: html }} />
                        ) : (
                            <p className="text-sm italic text-muted-foreground">Empty — select Edit to write.</p>
                        )}
                        {!isLine && block.updatedByName ? (
                            <p className="mt-1 text-[11px] text-muted-foreground">
                                Edited by {block.updatedByName} · {new Date(block.updatedAt).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                            </p>
                        ) : null}
                    </div>
                    {lock ? <LockBadge name={lock.displayName} /> : null}
                    <div className="flex shrink-0 gap-0.5 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                        <button type="button" onClick={() => void ed.begin()} disabled={Boolean(lock)} aria-label={`Edit ${label}`} title="Edit" className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent disabled:opacity-40">
                            <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={() => setConfirmDelete(true)} disabled={Boolean(lock)} aria-label={`Delete ${label}`} title="Delete" className="flex h-7 w-7 items-center justify-center rounded-md text-destructive hover:bg-accent disabled:opacity-40">
                            <Trash2 className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            )}
            {confirmDelete ? (
                <DeleteConfirm
                    what={label}
                    onCancel={() => setConfirmDelete(false)}
                    onConfirm={async () => {
                        setConfirmDelete(false);
                        const r = await ctx.remove(block);
                        if (!r.ok) ed.setError(r.current ? "It changed in the meantime — check it and try again." : r.message);
                    }}
                />
            ) : null}
            {ed.error ? <p role="alert" className="mt-1 text-xs text-destructive">{ed.error}</p> : null}
        </div>
    );
}

function DeleteConfirm({ what, onConfirm, onCancel }: { what: string; onConfirm: () => void | Promise<void>; onCancel: () => void }) {
    return (
        <div role="alertdialog" aria-label={`Delete ${what}`} className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span>Delete this {what} for everyone?</span>
            <button type="button" autoFocus onClick={() => void onConfirm()} className="h-7 rounded-md bg-destructive px-3 font-semibold text-destructive-foreground">Delete</button>
            <button type="button" onClick={onCancel} className="h-7 rounded-md border px-3 hover:bg-accent">Cancel</button>
        </div>
    );
}

function AssigneeSelect({ value, fallbackName, ctx, onChange, disabled, id }: {
    value: string | null | undefined;
    fallbackName?: string | null;
    ctx: NotesCtx;
    onChange: (userId: string | null) => void;
    disabled?: boolean;
    id?: string;
}) {
    const options = ctx.members.filter((m) => m.userId);
    const current = value ? normalizeUserId(value) : "";
    const listed = options.some((m) => normalizeUserId(m.userId) === current);
    return (
        <select
            id={id}
            value={current}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value || null)}
            aria-label="Assigned to"
            className="h-8 max-w-[11rem] rounded-md border bg-background px-1.5 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50"
        >
            <option value="">Unassigned</option>
            {current && !listed ? <option value={current}>{fallbackName || "Someone"}</option> : null}
            {options.map((m) => {
                const uid = normalizeUserId(m.userId);
                return (
                    <option key={uid} value={uid}>
                        {m.displayName || m.email}{uid === ctx.myUserId ? " (you)" : ""}
                    </option>
                );
            })}
        </select>
    );
}

function ActionRow({ block, ctx }: { block: MeetingNoteBlock; ctx: NotesCtx }) {
    const ed = useBlockEditor(block, ctx);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [saving, setSaving] = useState(false);
    const lock = ctx.lockOf(block.id);
    const overdue = Boolean(block.dueDate) && !block.done && block.dueDate! < todayIso();

    // Done / assignee / due date save at once (no lock needed for a single field).
    const change = async (changes: Changes) => {
        setSaving(true);
        ed.setError(null);
        const r = await ctx.save(block, changes);
        setSaving(false);
        if (!r.ok) ed.setError(r.current ? `${r.current.updatedByName || "Someone"} changed this item — it has been reloaded.` : r.message);
    };

    const saveText = () => {
        if (!ed.draft.trim()) {
            ed.setError("Type something, or delete the item.");
            return;
        }
        void ed.submit({ text: ed.draft });
    };

    return (
        <li className={cn("group rounded-md border bg-card px-2 py-1.5", ed.editing && "border-primary")}>
            {ed.conflict ? <ConflictNotice current={ed.conflict} /> : null}
            <div className="flex flex-wrap items-center gap-2">
                <input
                    type="checkbox"
                    checked={block.done}
                    disabled={saving}
                    onChange={(e) => void change({ done: e.target.checked })}
                    aria-label={`${block.text} — ${block.done ? "done" : "not done"}`}
                    className="h-4 w-4 shrink-0 accent-primary"
                />
                {ed.editing ? (
                    <input
                        autoFocus
                        value={ed.draft}
                        maxLength={500}
                        aria-label="Action item"
                        onChange={(e) => ed.setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); saveText(); }
                            if (e.key === "Escape") { e.preventDefault(); ed.end(); }
                        }}
                        onBlur={() => { if (!ed.busy) saveText(); }}
                        className="h-8 min-w-[8rem] flex-1 rounded-md border bg-background px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                ) : (
                    <span
                        className={cn("min-w-[8rem] flex-1 break-words text-sm", block.done && "text-muted-foreground line-through")}
                        onDoubleClick={() => { if (!lock) void ed.begin(); }}
                    >
                        {block.text}
                    </span>
                )}
                {lock ? <LockBadge name={lock.displayName} /> : null}
                <AssigneeSelect
                    value={block.assigneeUserId}
                    fallbackName={block.assigneeName}
                    ctx={ctx}
                    disabled={saving}
                    onChange={(userId) => void change({ assigneeUserId: userId })}
                />
                <input
                    type="date"
                    value={block.dueDate ?? ""}
                    disabled={saving}
                    onChange={(e) => void change({ dueDate: e.target.value || null })}
                    aria-label={overdue ? "Due date (overdue)" : "Due date"}
                    className={cn(
                        "h-8 rounded-md border bg-background px-1.5 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50",
                        overdue && "border-destructive text-destructive"
                    )}
                />
                <div className="flex shrink-0 gap-0.5 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                    {!ed.editing ? (
                        <button type="button" onClick={() => void ed.begin()} disabled={Boolean(lock)} aria-label="Edit action item" title="Edit" className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent disabled:opacity-40">
                            <Pencil className="h-3.5 w-3.5" />
                        </button>
                    ) : null}
                    <button type="button" onClick={() => setConfirmDelete(true)} disabled={Boolean(lock)} aria-label="Delete action item" title="Delete" className="flex h-7 w-7 items-center justify-center rounded-md text-destructive hover:bg-accent disabled:opacity-40">
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
            {confirmDelete ? (
                <DeleteConfirm
                    what="action item"
                    onCancel={() => setConfirmDelete(false)}
                    onConfirm={async () => {
                        setConfirmDelete(false);
                        const r = await ctx.remove(block);
                        if (!r.ok) ed.setError(r.current ? "It changed in the meantime — check it and try again." : r.message);
                    }}
                />
            ) : null}
            {ed.error ? <p role="alert" className="mt-1 text-xs text-destructive">{ed.error}</p> : null}
        </li>
    );
}

function errorText(err: unknown): string {
    return err instanceof ConnectApiError || err instanceof Error ? err.message : "Couldn't add it.";
}

function AddLine({ ctx, kind, placeholder }: { ctx: NotesCtx; kind: MeetingNoteKind; placeholder: string }) {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!text.trim() || busy) return;
        setBusy(true);
        setError(null);
        try {
            await ctx.create(kind, text.trim());
            setText("");
        } catch (err) {
            setError(errorText(err));
        } finally {
            setBusy(false);
        }
    };
    return (
        <form onSubmit={(e) => void submit(e)} className="space-y-1">
            <div className="flex gap-2">
                <input
                    value={text}
                    maxLength={500}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={placeholder}
                    aria-label={placeholder}
                    className="h-8 min-w-0 flex-1 rounded-md border bg-background px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
                <button type="submit" disabled={!text.trim() || busy} className="inline-flex h-8 items-center gap-1 rounded-md border px-3 text-xs font-medium hover:bg-accent disabled:opacity-50">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
                </button>
            </div>
            {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
        </form>
    );
}

function AddNoteSection({ ctx, empty }: { ctx: NotesCtx; empty: boolean }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const add = async () => {
        setBusy(true);
        setError(null);
        try {
            const b = await ctx.create("note", "");
            ctx.setAutoEdit(b.id);
        } catch (err) {
            setError(errorText(err));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="space-y-1">
            {empty ? <p className="text-xs text-muted-foreground">No notes yet. Everyone in the meeting can add and edit them.</p> : null}
            <button type="button" onClick={() => void add()} disabled={busy} className="inline-flex h-8 items-center gap-1 rounded-md border px-3 text-xs font-medium hover:bg-accent disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add notes
            </button>
            {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
        </div>
    );
}

function AddAction({ ctx }: { ctx: NotesCtx }) {
    const [text, setText] = useState("");
    const [assignee, setAssignee] = useState<string | null>(null);
    const [due, setDue] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!text.trim() || busy) return;
        setBusy(true);
        setError(null);
        try {
            await ctx.create("action", text.trim(), { assigneeUserId: assignee, dueDate: due || null });
            setText("");
            setAssignee(null);
            setDue("");
        } catch (err) {
            setError(errorText(err));
        } finally {
            setBusy(false);
        }
    };
    return (
        <form onSubmit={(e) => void submit(e)} className="space-y-1">
            <div className="flex flex-wrap gap-2">
                <input
                    value={text}
                    maxLength={500}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Add an action item"
                    aria-label="New action item"
                    className="h-8 min-w-[10rem] flex-1 rounded-md border bg-background px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
                <AssigneeSelect value={assignee} ctx={ctx} onChange={setAssignee} />
                <input
                    type="date"
                    value={due}
                    onChange={(e) => setDue(e.target.value)}
                    aria-label="Due date for the new action item"
                    className="h-8 rounded-md border bg-background px-1.5 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
                <button type="submit" disabled={!text.trim() || busy} className="inline-flex h-8 items-center gap-1 rounded-md border px-3 text-xs font-medium hover:bg-accent disabled:opacity-50">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
                </button>
            </div>
            {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
            <p className="text-[11px] text-muted-foreground">Whoever you assign sees it in their Activity feed.</p>
        </form>
    );
}
